import { useEffect, useState, useRef, useCallback } from 'react';
import { Command } from 'cmdk';
import { Search, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useGlobalCommandPalette } from './useGlobalCommandPalette';
import { navigationCommands } from './commands/navigation';
import { actionCommands } from './commands/actions';
import { useRecentItems } from './useRecentItems';
import { globalSearch, type SearchResult } from '@/lib/searchApi';
import type { GlobalCommand } from './types';

export interface CommandPaletteContentProps {
    onClose: (open: boolean) => void;
}

export function CommandPaletteContent({ onClose }: CommandPaletteContentProps) {
    const [search, setSearch] = useState('');
    const [page, setPage] = useState<'root' | 'search'>('root');
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const navigate = useNavigate();
    const { recentItems, addRecentItem } = useRecentItems();
    const { isOpen } = useGlobalCommandPalette();

    // Debounced search function
    const performSearch = useCallback(async (query: string) => {
        if (!query || query.length < 2) {
            setSearchResults([]);
            setIsSearching(false);
            return;
        }

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();
        setIsSearching(true);

        try {
            const results = await globalSearch(query, 'all', 10, abortControllerRef.current.signal);
            setSearchResults(results);
        } catch (error) {
            console.error('Search failed:', error);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    }, []);

    // Auto-focus search input when opened
    useEffect(() => {
        if (isOpen && inputRef.current) {
            const timeoutId = setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
            return () => clearTimeout(timeoutId);
        }
    }, [isOpen]);

    // Reset state when closed
    useEffect(() => {
        if (!isOpen) {
            setPage('root');
            setSearch('');
            setSearchResults([]);
        }
    }, [isOpen]);

    // Debounce search (300ms)
    useEffect(() => {
        if (!search || search.length < 2) {
            setSearchResults([]);
            setIsSearching(false);
            return;
        }

        const timeoutId = setTimeout(() => {
            performSearch(search);
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [search, performSearch]);

    const allCommands: GlobalCommand[] = [
        ...navigationCommands(navigate, onClose),
        ...actionCommands(onClose),
    ];

    const handleCommandSelect = (command: GlobalCommand) => {
        command.action();
    };

    const handleSearchResultSelect = (result: SearchResult) => {
        addRecentItem({
            id: result.id,
            type: result.type,
            title: result.title,
            subtitle: result.subtitle,
            url: result.url,
        });
        navigate(result.url);
        onClose(false);
    };

    const showSearchResults = search.length >= 2 && (searchResults.length > 0 || isSearching);

    return (
        <Command
            className="border-0 shadow-none rounded-lg"
            shouldFilter={false}
            onKeyDown={(e) => {
                if (e.key === 'Escape' && page !== 'root') {
                    e.preventDefault();
                    setPage('root');
                }
            }}
            role="combobox"
            aria-expanded={isOpen}
            aria-label="Command menu"
        >
            <div className="flex items-center border-b px-3">
                <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
                <Command.Input
                    ref={inputRef}
                    placeholder="Buscar comandos o navegar..."
                    value={search}
                    onValueChange={setSearch}
                    className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label="Search command"
                    autoFocus
                />
            </div>

            <Command.List className="max-h-[400px] overflow-y-auto p-2">
                <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
                    No se encontraron resultados.
                </Command.Empty>

                {page === 'root' && (
                    <>
                        {/* Recent Items */}
                        {recentItems.length > 0 && !search && (
                            <Command.Group heading="Recientes">
                                {recentItems.map((item) => (
                                    <Command.Item
                                        key={item.id}
                                        onSelect={() => {
                                            navigate(item.url);
                                            onClose(false);
                                        }}
                                        className="flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent"
                                    >
                                        <span className="font-medium">{item.title}</span>
                                        {item.subtitle && (
                                            <span className="text-xs text-muted-foreground">
                                                • {item.subtitle}
                                            </span>
                                        )}
                                    </Command.Item>
                                ))}
                            </Command.Group>
                        )}

                        {/* Navigation Commands */}
                        <Command.Group heading="Navegación">
                            {navigationCommands(navigate, onClose).map((cmd) => (
                                <Command.Item
                                    key={cmd.id}
                                    onSelect={() => handleCommandSelect(cmd)}
                                    className="flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent"
                                >
                                    {cmd.icon && <cmd.icon className="h-4 w-4" />}
                                    <span>{cmd.title}</span>
                                    {cmd.shortcut && (
                                        <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
                                            {cmd.shortcut}
                                        </kbd>
                                    )}
                                </Command.Item>
                            ))}
                        </Command.Group>

                        {/* Action Commands */}
                        <Command.Group heading="Acciones Rápidas">
                            {actionCommands(onClose).map((cmd) => (
                                <Command.Item
                                    key={cmd.id}
                                    onSelect={() => handleCommandSelect(cmd)}
                                    className="flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent"
                                >
                                    {cmd.icon && <cmd.icon className="h-4 w-4" />}
                                    <span>{cmd.title}</span>
                                    {cmd.shortcut && (
                                        <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
                                            {cmd.shortcut}
                                        </kbd>
                                    )}
                                </Command.Item>
                            ))}
                        </Command.Group>
                    </>
                )}

                {/* Search Results */}
                {showSearchResults && (
                    <>
                        {isSearching ? (
                            <div className="flex items-center justify-center py-6">
                                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                                <span className="ml-2 text-sm text-muted-foreground">Buscando...</span>
                            </div>
                        ) : (
                            <Command.Group heading={`Resultados (${searchResults.length})`}>
                                {searchResults.map((result) => (
                                    <Command.Item
                                        key={`${result.type}-${result.id}`}
                                        onSelect={() => handleSearchResultSelect(result)}
                                        className="flex flex-col gap-1 px-2 py-2 text-sm rounded-sm cursor-pointer hover:bg-accent"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium">{result.title}</span>
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                                {result.type}
                                            </span>
                                        </div>
                                        {result.subtitle && (
                                            <div className="text-xs text-muted-foreground">
                                                {result.subtitle}
                                            </div>
                                        )}
                                    </Command.Item>
                                ))}
                            </Command.Group>
                        )}
                    </>
                )}
            </Command.List>
        </Command>
    );
}
